const TIME_ZONE = 'America/Los_Angeles';
const MISSING_SORT_SENTINEL = '9999-12-31T23:59:59.999Z';

export const CUSTOMER_CONTAINER_VIEW_FIELDS = Object.freeze([
  'schema_version', 'container_id', 'customer_uid', 'archived_by_restore',
  'dashboard_state', 'base_rank', 'sort_at', 'container_no', 'dc_container_no',
  'job_no', 'terminal_name', 'ssl', 'size_type', 'status', 'current_stage',
  'is_empty_returned', 'is_past_due', 'urgent_handle', 'eta_at', 'lfd_at',
  'due_at', 'pickup_appointment_at', 'delivery_appointment_at', 'load_out_at',
  'loaded_delivered_at', 'empty_ready_at', 'empty_picked_up_at', 'empty_returned_at',
]);

const STRING_FIELDS = new Set([
  'container_id', 'customer_uid', 'dashboard_state', 'container_no',
  'dc_container_no', 'job_no', 'terminal_name', 'ssl', 'size_type', 'status',
  'current_stage',
]);
const BOOLEAN_FIELDS = new Set([
  'archived_by_restore', 'is_empty_returned', 'is_past_due', 'urgent_handle',
]);
const DATE_FIELDS = new Set(CUSTOMER_CONTAINER_VIEW_FIELDS.filter((field) => field.endsWith('_at')));
DATE_FIELDS.add('sort_at');
const IN_PROGRESS_STATUSES = new Set([
  'dispatched', 'in_progress', 'loaded_delivered', 'empty_ready', 'returning_empty',
  'picked_up_from_terminal', 'arrived_at_warehouse', 'out_for_delivery',
]);

function validDate(value) {
  return value instanceof Date && Number.isFinite(value.getTime());
}

export function customerDate(value) {
  if (value == null) return null;
  try {
    if (value instanceof Date) return validDate(value) ? new Date(value.getTime()) : null;
    if (typeof value === 'number' || typeof value === 'string') {
      const date = new Date(value);
      return validDate(date) ? date : null;
    }
    if (typeof value?.toDate === 'function') {
      const date = value.toDate();
      return validDate(date) ? new Date(date.getTime()) : null;
    }
    const seconds = value?.seconds ?? value?._seconds;
    const nanoseconds = value?.nanoseconds ?? value?._nanoseconds ?? 0;
    if (Number.isInteger(seconds) && Number.isInteger(nanoseconds)
      && nanoseconds >= 0 && nanoseconds <= 999_999_999) {
      const date = new Date((seconds * 1000) + Math.floor(nanoseconds / 1_000_000));
      return validDate(date) ? date : null;
    }
  } catch (_) {
    return null;
  }
  return null;
}

function zonedParts(value, timeZone = TIME_ZONE) {
  return Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(value)
    .filter((part) => part.type !== 'literal')
    .map((part) => [part.type, Number(part.value)]));
}

function zonedBoundary(parts, timeZone = TIME_ZONE) {
  const target = Date.UTC(parts.year, parts.month - 1, parts.day, 0, 0, 0);
  let candidate = target;
  for (let iteration = 0; iteration < 4; iteration += 1) {
    const actual = zonedParts(new Date(candidate), timeZone);
    const represented = Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute, actual.second);
    candidate += target - represented;
  }
  return new Date(candidate);
}

function shiftCalendarDay(parts, days) {
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1, day: shifted.getUTCDate() };
}

export function customerDashboardWindows(now = new Date()) {
  if (!validDate(now)) throw new TypeError('A valid current date is required.');
  const current = zonedParts(now);
  const today = { year: current.year, month: current.month, day: current.day };
  return Object.freeze({
    todayStart: zonedBoundary(today),
    tomorrowStart: zonedBoundary(shiftCalendarDay(today, 1)),
    dayAfterTomorrowStart: zonedBoundary(shiftCalendarDay(today, 2)),
    dueUpper: zonedBoundary(shiftCalendarDay(today, 3)),
    recentStart: zonedBoundary(shiftCalendarDay(today, -29)),
    recentEnd: zonedBoundary(shiftCalendarDay(today, 1)),
  });
}

export function canonicalCustomerCompletion(item) {
  return item?.dashboard_state === 'completed'
    && item?.status === 'empty_returned'
    && item?.is_empty_returned === true
    && customerDate(item?.empty_returned_at) !== null;
}

function unavailable() {
  throw new TypeError('Customer container data is unavailable.');
}

export function sanitizeCustomerContainerView(value, { uid } = {}) {
  if (!value || typeof value !== 'object' || Array.isArray(value) || typeof uid !== 'string' || !uid) unavailable();
  const result = {};
  for (const field of CUSTOMER_CONTAINER_VIEW_FIELDS) {
    const fieldValue = value[field];
    if (STRING_FIELDS.has(field) && typeof fieldValue !== 'string') unavailable();
    if (BOOLEAN_FIELDS.has(field) && typeof fieldValue !== 'boolean') unavailable();
    if (DATE_FIELDS.has(field) && fieldValue !== null && !customerDate(fieldValue)) unavailable();
    if (field === 'schema_version' && fieldValue !== 1) unavailable();
    if (field === 'base_rank' && (!Number.isInteger(fieldValue) || fieldValue < 0 || fieldValue > 3)) unavailable();
    result[field] = fieldValue;
  }
  if (result.customer_uid !== uid || result.archived_by_restore !== false) unavailable();
  if (result.container_id !== value.id && typeof value.id === 'string') unavailable();
  if (!['operational', 'completed'].includes(result.dashboard_state)) unavailable();
  if (result.dashboard_state === 'completed'
    && (result.base_rank !== 3 || !canonicalCustomerCompletion(result))) unavailable();
  if (result.dashboard_state === 'operational'
    && (result.base_rank > 2 || canonicalCustomerCompletion(result) || customerDate(result.sort_at) === null)) unavailable();
  return Object.freeze({ id: result.container_id, ...result });
}

function visibleDate(value) {
  const date = customerDate(value);
  return date?.toISOString() === MISSING_SORT_SENTINEL ? null : date;
}

function compareItems(left, right, dateField, newestFirst = false) {
  const leftDate = visibleDate(left[dateField]);
  const rightDate = visibleDate(right[dateField]);
  if (leftDate && rightDate && leftDate.getTime() !== rightDate.getTime()) {
    return newestFirst ? rightDate - leftDate : leftDate - rightDate;
  }
  if (leftDate && !rightDate) return -1;
  if (!leftDate && rightDate) return 1;
  const containerOrder = String(left.container_no).localeCompare(String(right.container_no), 'en', { numeric: true });
  return containerOrder || String(left.id).localeCompare(String(right.id), 'en', { numeric: true });
}

export function groupCustomerContainerViews(items, { now = new Date() } = {}) {
  const windows = customerDashboardWindows(now);
  const groups = [
    { key: 'attention', items: [] },
    { key: 'in_progress', items: [] },
    { key: 'upcoming', items: [] },
    { key: 'completed_recent', items: [] },
  ];
  const seen = new Set();
  for (const item of Array.isArray(items) ? items : []) {
    if (!item?.id || seen.has(item.id)) continue;
    seen.add(item.id);
    if (canonicalCustomerCompletion(item)) {
      const completedAt = customerDate(item.empty_returned_at);
      if (completedAt >= windows.recentStart && completedAt < windows.recentEnd) groups[3].items.push(item);
      continue;
    }
    if (item.dashboard_state !== 'operational') continue;
    const dueAt = customerDate(item.due_at);
    if (item.base_rank === 0 || item.is_past_due === true || item.urgent_handle === true
      || (dueAt && dueAt < windows.dueUpper)) {
      groups[0].items.push(item);
    } else if (item.base_rank === 1 || IN_PROGRESS_STATUSES.has(item.status)) {
      groups[1].items.push(item);
    } else {
      groups[2].items.push(item);
    }
  }
  for (const group of groups) {
    const dateField = group.key === 'completed_recent' ? 'empty_returned_at' : 'sort_at';
    group.items.sort((left, right) => compareItems(left, right, dateField, group.key === 'completed_recent'));
  }
  return groups;
}

export function mergeCustomerPages(existing, page) {
  if (!page || !Array.isArray(page.items) || typeof page.maybeMore !== 'boolean'
    || typeof page.cursorUnavailable !== 'boolean') {
    throw new TypeError('Customer dashboard pagination response is invalid.');
  }
  if (page.maybeMore && !page.cursorUnavailable && typeof page.nextCursor !== 'string') {
    throw new TypeError('Customer dashboard pagination cursor is invalid.');
  }
  const items = [];
  const seen = new Set();
  for (const item of [...(Array.isArray(existing) ? existing : []), ...page.items]) {
    if (!item?.id || seen.has(item.id)) continue;
    seen.add(item.id);
    items.push(item);
  }
  return Object.freeze({
    items,
    nextCursor: typeof page.nextCursor === 'string' ? page.nextCursor : null,
    maybeMore: page.maybeMore,
    cursorUnavailable: page.cursorUnavailable,
  });
}
